import * as React from "react";

import type {PartialPageSettings} from "#data-access/page-settings.ts";
import {usePageSettings} from "#data-access/use-page-settings.ts";

import DefaultPageTemplate from "./page-templates/default-page-template.tsx";

/**
 * If we have an error status code, we want to render a full page error with
 * the default page template.
 */
const isFullPageError = (statusCode: number): boolean =>
    statusCode < 200 || statusCode >= 400;

type Props = {
    // The contents of the page
    children: React.ReactNode;

    // The template to use to render the page. Defaults to DefaultPageTemplate.
    template?: React.ComponentType<{children: React.ReactNode}>;

    // The settings to apply to the page. If provided, the settings will be
    // updated before rendering the page.
    settings?: PartialPageSettings;

    // Whether we want to reset page settings. Defaults true.
    resetSettings?: boolean;
};

/**
 * A component that renders the root of a page on khanacademy.org, including
 * the default page template (site header, footer, and shared styles) or a
 * custom template if provided.
 */
const PageRoot = ({
    children,
    settings,
    resetSettings = true,
    template = DefaultPageTemplate,
}: Props): React.ReactElement => {
    const {
        settings: {statusCode},
        update,
    } = usePageSettings();

    if (settings) {
        update(settings, resetSettings);
    }

    // Full-page errors render their own page template
    if (isFullPageError(statusCode)) {
        return <>{children}</>;
    }
    const Template = template;
    return <Template>{children}</Template>;
};

export default PageRoot;
